import { Typography } from '@neo/primitives/typography'
import heic2any from 'heic2any'
import { useCallback, useEffect, useState } from 'react'
import { executionService } from 'v2/shared/services/execution-service'
import { logger } from 'v2/utils/logger'
import type { MediaProps } from './types'

// Define file types as a constant object
const FILE_TYPE = {
  HEIC: 'image/heic',
  JPEG: 'image/jpeg',
} as const

interface WhatsAppMediaProps extends Pick<MediaProps, 'file' | 'senderId' | 'contactUuid'> {
  onMediaLoaded: (url: string) => void
}

export const WhatsAppMedia = ({
  file,
  senderId,
  contactUuid,
  onMediaLoaded,
}: WhatsAppMediaProps) => {
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [objectUrl, setObjectUrl] = useState<string | null>(null)

  const cleanupObjectUrl = useCallback(() => {
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl)
      setObjectUrl(null)
    }
  }, [objectUrl])

  const fetchWhatsAppMedia = useCallback(async () => {
    if (!senderId || !file.file_id) return

    setIsLoading(true)
    setError(null)
    cleanupObjectUrl() // Clean up previous object URL if exists

    try {
      // Fetch the media blob from the WhatsApp API
      const blob = await executionService.getWhatsAppMedia(senderId, file.file_id, contactUuid)
      let mediaBlob: Blob = blob

      // Convert HEIC format to JPEG if necessary
      if (blob.type === FILE_TYPE.HEIC) {
        const convertedBlob = await heic2any({
          blob: mediaBlob,
          toType: FILE_TYPE.JPEG,
        })
        // Ensure the converted blob is of type Blob
        mediaBlob =
          convertedBlob instanceof Blob
            ? convertedBlob
            : new Blob([convertedBlob as unknown as BlobPart], { type: FILE_TYPE.JPEG })
      }

      // Create a URL for the media blob with the correct MIME type
      const url = URL.createObjectURL(new Blob([mediaBlob], { type: file.file_type }))
      setObjectUrl(url)
      onMediaLoaded(url)
    } catch (e) {
      logger.warn('Failed to load media', { error: e })
      setError('Failed to load media.')
    } finally {
      setIsLoading(false)
    }
  }, [file.file_id, file.file_type, senderId, contactUuid, onMediaLoaded, cleanupObjectUrl])

  useEffect(() => {
    fetchWhatsAppMedia()
    return cleanupObjectUrl
  }, [fetchWhatsAppMedia, cleanupObjectUrl])

  if (isLoading)
    return (
      <Typography variant="S/400" className="text-secondary">
        Loading media...
      </Typography>
    )

  if (error) {
    return (
      <Typography variant="S/400" className="text-status-danger">
        {error}{' '}
        <button
          type="button"
          onClick={fetchWhatsAppMedia}
          className="underline cursor-pointer rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid="button-retry-media"
        >
          Retry
        </button>
      </Typography>
    )
  }

  // Return null when media is loaded (handled by parent through onMediaLoaded)
  return null
}
